import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  useCarrierCallback,
  useCarrierStatus,
  useConnectCarrier,
  useConnectCarrierClientCredentials,
  useDisconnectCarrier,
} from '@/api/domains/carriers';
import type {
  CarrierClientCredentialsPayload,
  CarrierConnectionStatus,
} from '@/api/types/carriers';
import { isInsideShopifyAdmin } from '@/lib/app-bridge';
import { toApiProblem, toastProblem } from '@/lib/errors';

const OAUTH_POPUP_NAME = 'inpost-oauth';
const OAUTH_MESSAGE_TYPE = 'inpost-oauth-callback';

type OAuthMessage = {
  type: typeof OAUTH_MESSAGE_TYPE;
  code?: string | null;
  state?: string | null;
  error?: string | null;
};

function isOAuthMessage(value: unknown): value is OAuthMessage {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { type?: unknown }).type === OAUTH_MESSAGE_TYPE
  );
}

function openOAuthPopup(url: string): Window | null {
  const width = 640;
  const height = 720;
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
  const features = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`;
  return window.open(url, OAUTH_POPUP_NAME, features);
}

export type UseInPostConnect = {
  status: CarrierConnectionStatus | undefined;
  isConnected: boolean;
  needsOrgSelection: boolean;
  isStatusLoading: boolean;
  isStatusError: boolean;
  isConnecting: boolean;
  isConnectingClientCredentials: boolean;
  isDisconnecting: boolean;
  isBusy: boolean;
  /** Whether the browser "Sign in with InPost" (PKCE) flow is offered to this shop's market. */
  canUseOAuthLogin: boolean;
  connect: () => void;
  connectWithClientCredentials: (payload: CarrierClientCredentialsPayload) => void;
  disconnect: () => void;
};

/**
 * Shared InPost OAuth connect flow: kicks off the authorize URL (popup when
 * embedded, full redirect otherwise), listens for the popup's postMessage
 * callback, exchanges the code, and routes to organization selection when the
 * connection needs one. Used by the Authorization page and the setup wizard.
 */
export function useInPostConnect(): UseInPostConnect {
  const { t } = useTranslation('authorization');
  const { t: tCarriers } = useTranslation('carriers');
  const navigate = useNavigate();
  const popupRef = useRef<Window | null>(null);

  const statusQuery = useCarrierStatus('inpost');
  const connectMutation = useConnectCarrier('inpost');
  const clientCredentialsMutation = useConnectCarrierClientCredentials('inpost');
  const disconnectMutation = useDisconnectCarrier('inpost');
  const callback = useCarrierCallback('inpost');

  const status = statusQuery.data;
  const isConnected = status?.status === 'connected';
  const needsOrgSelection = status?.needs_organization_selection === true;
  // Fail open while the status is loading or when the field is absent (stale
  // cached response / backend not yet redeployed) - the backend enforces the gate.
  const canUseOAuthLogin = status?.allowed_auth_methods?.includes('authorization_code') ?? true;
  const isBusy =
    connectMutation.isPending ||
    clientCredentialsMutation.isPending ||
    disconnectMutation.isPending ||
    callback.isPending;

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      // Only accept messages from the OAuth popup this hook opened.
      if (popupRef.current === null || event.source !== popupRef.current) return;
      if (!isOAuthMessage(event.data)) return;

      const { code, state, error } = event.data;

      if (error) {
        toast.error(tCarriers('connect.callback.inpost.rejected', { error }));
        return;
      }
      if (!code || !state) {
        toast.error(tCarriers('connect.callback.inpost.missingParams'));
        return;
      }

      callback.mutate(
        { code, state },
        {
          onSuccess: (result) => {
            if (result.needs_organization_selection) {
              toast.success(tCarriers('connect.callback.inpost.needsOrgToast'));
              navigate('/connect/inpost/select-organization');
            } else {
              toast.success(tCarriers('connect.callback.inpost.connectedToast'));
            }
          },
          onError: (err) => toastProblem(toApiProblem(err)),
        },
      );
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [callback, navigate, tCarriers]);

  const connect = () =>
    connectMutation.mutate(undefined, {
      onSuccess: ({ authorize_url }) => {
        if (isInsideShopifyAdmin()) {
          const popup = openOAuthPopup(authorize_url);
          if (popup) {
            popupRef.current = popup;
            popup.focus();
            return;
          }
          window.open(authorize_url, '_top');
          return;
        }
        window.location.href = authorize_url;
      },
      onError: (error) => {
        const problem = toApiProblem(error);
        toast.error(t('login.toast.connectFailed'), {
          description: problem.detail ?? problem.title,
        });
      },
    });

  const connectWithClientCredentials = (payload: CarrierClientCredentialsPayload) =>
    clientCredentialsMutation.mutate(payload, {
      onSuccess: (result) => {
        if (result.needs_organization_selection) {
          toast.success(tCarriers('connect.callback.inpost.needsOrgToast'));
          navigate('/connect/inpost/select-organization');
        } else {
          toast.success(tCarriers('connect.callback.inpost.connectedToast'));
        }
      },
      onError: (error) => toastProblem(toApiProblem(error)),
    });

  const disconnect = () =>
    disconnectMutation.mutate(undefined, {
      onSuccess: () => toast.success(t('login.toast.disconnected')),
      onError: (error) => toastProblem(toApiProblem(error)),
    });

  return {
    status,
    isConnected,
    needsOrgSelection,
    isStatusLoading: statusQuery.isLoading,
    isStatusError: statusQuery.isError,
    isConnecting: connectMutation.isPending,
    isConnectingClientCredentials: clientCredentialsMutation.isPending,
    isDisconnecting: disconnectMutation.isPending,
    isBusy,
    canUseOAuthLogin,
    connect,
    connectWithClientCredentials,
    disconnect,
  };
}
